import type { ChangeEventHandler, InputHTMLAttributes } from "react";

import { useMeasure } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "motion/react";
import { forwardRef, useState } from "react";
import { cn } from "../utils/classnames";

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

const labelVariants = {
  touched: {
    fontSize: "12px",
    opacity: "100%",
    top: "-8px",
  },
  untouched: {
    backgroundColor: "rgba(0,0,0,0)",
    fontSize: "14px",
    opacity: "0%",
    top: "16px",
  },
};

const backgroundVariants = {
  touched: ({ width, height }: { width: number; height: number }) => ({
    backgroundColor: "#fff",
    height: `${height}px`,
    top: "-8px",
    width: `${width}px`,
  }),
  untouched: {
    height: "14px",
    top: "-8px",
    width: "0%",
  },
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onChange, value, defaultValue, ...props }, ref) => {
    // defaultValue seeds an uncontrolled input's initial text (e.g. a
    // hydrated LFB step field) — without reading it here, the input would
    // render with text but no floating label until the user's first keystroke.
    const [isTouched, setIsTouched] = useState<boolean>(
      (value != null && value !== "") ||
        (defaultValue != null && defaultValue !== ""),
    );
    const [labelRef, { width, height }] = useMeasure();

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      if (onChange) {
        onChange(e);
      }
      setIsTouched(e?.target?.value !== "");
    };

    return (
      <motion.div
        className="group relative"
        transition={{ staggerChildren: 1 }}
      >
        <input
          className={cn(
            `z-50 flex h-12 w-full rounded-sm border border-new-beginnings-base bg-white px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-new-beginnings-base disabled:cursor-not-allowed disabled:opacity-50`,
            className,
          )}
          defaultValue={defaultValue}
          onChange={(e) => handleChange(e)}
          ref={ref}
          type={type}
          value={value}
          {...props}
        />
        <motion.div
          animate={isTouched ? "touched" : "untouched"}
          className={cn("absolute left-[9px] z-[40] min-w-fit text-xs")}
          custom={{ height, width }}
          initial="untouched"
          variants={backgroundVariants}
        />
        <AnimatePresence>
          {isTouched && (
            <motion.label
              animate={isTouched ? "touched" : "untouched"}
              className={cn(
                "absolute left-[9px] z-[40] px-1 text-new-beginnings-base text-xs",
              )}
              exit="untouched"
              initial="untouched"
              ref={labelRef}
              variants={labelVariants}
            >
              {props?.placeholder}
            </motion.label>
          )}
        </AnimatePresence>
      </motion.div>
    );
  },
);
Input.displayName = "Input";

export { Input };
